import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import MobileBottomNav from "@/components/layout/MobileBottomNav";
import MobileTopHeader from "@/components/layout/MobileTopHeader";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import WorkerRoute from "@/components/WorkerRoute";
import SuperAdminRoute from "@/components/SuperAdminRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import PageVisitTracker from "@/components/analytics/PageVisitTracker";

// Device-aware routing — picks mobile or desktop page automatically
import { IndexPage, DiscoverPage, MatchesPage, MessagesPage, DashboardPage } from "./router/DeviceRouter";

// Lazy-load all other pages for code splitting
const Signup = lazy(() => import("./pages/Signup"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ProfileView = lazy(() => import("./pages/ProfileView"));
const Pricing = lazy(() => import("./pages/Pricing"));
const SafetyCenter = lazy(() => import("./pages/SafetyCenter"));
const Chat = lazy(() => import("./pages/Chat"));
const Activity = lazy(() => import("./pages/Activity"));
const ProfileDashboard = lazy(() => import("./pages/ProfileDashboard"));
const Settings = lazy(() => import("./pages/Settings"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const HelpFAQ = lazy(() => import("./pages/HelpFAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const CommunityGuidelines = lazy(() => import("./pages/CommunityGuidelines"));
const SuccessStories = lazy(() => import("./pages/SuccessStories"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminPhotos = lazy(() => import("./pages/admin/AdminPhotos"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminVerifications = lazy(() => import("./pages/admin/AdminVerifications"));
const AdminAudit = lazy(() => import("./pages/admin/AdminAudit"));
const AdminCredits = lazy(() => import("./pages/admin/AdminCredits"));
const AdminFinance = lazy(() => import("./pages/admin/AdminFinance"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminOnline = lazy(() => import("./pages/admin/AdminOnline"));
const AdminFlagged = lazy(() => import("./pages/admin/AdminFlagged"));
const AdminUserActivity = lazy(() => import("./pages/admin/AdminUserActivity"));
const AdminHero = lazy(() => import("./pages/admin/AdminHero"));
const AdminScamDetection = lazy(() => import("./pages/admin/AdminScamDetection"));
const AdminProfileFields = lazy(() => import("./pages/admin/AdminProfileFields"));
const AdminSupport = lazy(() => import("./pages/admin/AdminSupport"));
const AdminLaunchChecklist = lazy(() => import("./pages/admin/AdminLaunchChecklist"));
const AdminEmailTemplates = lazy(() => import("./pages/admin/AdminEmailTemplates"));
const AdminWorkers = lazy(() => import("./pages/admin/AdminWorkers"));
const AdminMatches = lazy(() => import("./pages/admin/AdminMatches"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminNudgeEmails = lazy(() => import("./pages/admin/AdminNudgeEmails"));
const AdminLiveVisitors = lazy(() => import("./pages/admin/AdminLiveVisitors"));
const AdminTraffic = lazy(() => import("./pages/admin/AdminTraffic"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const WorkerLogin = lazy(() => import("./pages/worker/WorkerLogin"));
const WorkerDashboard = lazy(() => import("./pages/worker/WorkerDashboard"));
const WorkerProfileView = lazy(() => import("./pages/worker/WorkerProfileView"));
const WorkerUsers = lazy(() => import("./pages/worker/WorkerUsers"));
const WorkerChatInbox = lazy(() => import("./pages/worker/WorkerChatInbox"));
const WorkerChatView = lazy(() => import("./pages/worker/WorkerChatView"));
const Support = lazy(() => import("./pages/Support"));
const Swipe = lazy(() => import("./pages/Swipe"));
const Quiz = lazy(() => import("./pages/Quiz"));
const VIP = lazy(() => import("./pages/VIP"));
const Credits = lazy(() => import("./pages/Credits"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const BillingPolicy = lazy(() => import("./pages/BillingPolicy"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const PhLanding = lazy(() => import("./pages/mobile/PhLanding"));
const UkLanding = lazy(() => import("./pages/mobile/UkLanding"));
const AuLanding = lazy(() => import("./pages/mobile/AuLanding"));
const CaLanding = lazy(() => import("./pages/mobile/CaLanding"));
const InLanding = lazy(() => import("./pages/mobile/InLanding"));
const PkLanding = lazy(() => import("./pages/mobile/PkLanding"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <p className="text-sm text-muted-foreground">Loading…</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <PageVisitTracker />
            <Routes>
              <Route path="/" element={<IndexPage />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/Sign" element={<Navigate to="/signup" replace />} />
              <Route path="/Signup" element={<Navigate to="/signup" replace />} />
              <Route path="/SignUp" element={<Navigate to="/signup" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/Login" element={<Navigate to="/login" replace />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="/discover" element={<ProtectedRoute><DiscoverPage /></ProtectedRoute>} />
              <Route path="/swipe" element={<ProtectedRoute><Swipe /></ProtectedRoute>} />
              <Route path="/quiz" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
              <Route path="/profile/:id" element={<ProfileView />} />
              <Route path="/member/:slug" element={<PublicProfile />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/ph-landing" element={<PhLanding />} />
              <Route path="/uk-landing" element={<UkLanding />} />
              <Route path="/au-landing" element={<AuLanding />} />
              <Route path="/ca-landing" element={<CaLanding />} />
              <Route path="/in-landing" element={<InLanding />} />
              <Route path="/pk-landing" element={<PkLanding />} />
              <Route path="/vip" element={<VIP />} />
              <Route path="/credits" element={<Credits />} />
              <Route path="/safety" element={<SafetyCenter />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/help" element={<HelpFAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/guidelines" element={<CommunityGuidelines />} />
              <Route path="/refund" element={<RefundPolicy />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/billing" element={<BillingPolicy />} />
              <Route path="/success-stories" element={<SuccessStories />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/support" element={<Support />} />
              <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
              <Route path="/chat/:id" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
              <Route path="/matches" element={<ProtectedRoute><MatchesPage /></ProtectedRoute>} />
              <Route path="/activity" element={<ProtectedRoute><Activity /></ProtectedRoute>} />
              <Route path="/profile/edit" element={<ProtectedRoute><ProfileDashboard /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/online" element={<AdminRoute><AdminOnline /></AdminRoute>} />
              <Route path="/admin/flagged" element={<AdminRoute><AdminFlagged /></AdminRoute>} />
              <Route path="/admin/activity" element={<AdminRoute><AdminUserActivity /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
              <Route path="/admin/photos" element={<AdminRoute><AdminPhotos /></AdminRoute>} />
              <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
              <Route path="/admin/verifications" element={<AdminRoute><AdminVerifications /></AdminRoute>} />
              <Route path="/admin/audit" element={<AdminRoute><AdminAudit /></AdminRoute>} />
              <Route path="/admin/credits" element={<AdminRoute><AdminCredits /></AdminRoute>} />
              <Route path="/admin/finance" element={<AdminRoute><AdminFinance /></AdminRoute>} />
              <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
              <Route path="/admin/hero" element={<AdminRoute><AdminHero /></AdminRoute>} />
              <Route path="/admin/scam" element={<AdminRoute><AdminScamDetection /></AdminRoute>} />
              <Route path="/admin/profile-fields" element={<AdminRoute><AdminProfileFields /></AdminRoute>} />
              <Route path="/admin/support" element={<AdminRoute><AdminSupport /></AdminRoute>} />
              <Route path="/admin/launch" element={<AdminRoute><AdminLaunchChecklist /></AdminRoute>} />
              <Route path="/admin/emails" element={<AdminRoute><AdminEmailTemplates /></AdminRoute>} />
              <Route path="/admin/nudge-emails" element={<AdminRoute><AdminNudgeEmails /></AdminRoute>} />
              <Route path="/admin/live" element={<AdminRoute><AdminLiveVisitors /></AdminRoute>} />
              <Route path="/admin/traffic" element={<AdminRoute><AdminTraffic /></AdminRoute>} />
              <Route path="/admin/workers" element={<SuperAdminRoute><AdminWorkers /></SuperAdminRoute>} />
              <Route path="/admin/matches" element={<AdminRoute><AdminMatches /></AdminRoute>} />
              <Route path="/admin/messages" element={<AdminRoute><AdminMessages /></AdminRoute>} />
              <Route path="/worker/login" element={<WorkerLogin />} />
              <Route path="/worker/dashboard" element={<WorkerRoute><WorkerDashboard /></WorkerRoute>} />
              <Route path="/worker/profile/:userId" element={<WorkerRoute><WorkerProfileView /></WorkerRoute>} />
              <Route path="/worker/users" element={<WorkerRoute><WorkerUsers /></WorkerRoute>} />
              <Route path="/worker/chat/:userId" element={<WorkerRoute><WorkerChatInbox /></WorkerRoute>} />
              <Route path="/worker/chat/:userId/:conversationId" element={<WorkerRoute><WorkerChatView /></WorkerRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
          <MobileTopHeader />
          <MobileBottomNav />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>

);

export default App;
